import type { Bottle, DrinkKind } from '../../shared/api';

/** An open bottle below this level ("About ¼ left" and under in describeLevel) is worth checking. */
export const CHECK_THRESHOLD = 0.45;
/** ...unless it was checked by hand this recently and nothing has been poured since. */
export const RECHECK_AFTER_DAYS = 30;

export const SERVE_PRESETS: ReadonlyArray<{ label: string; ml: number | null }> = [
  { label: 'Shot (30ml)', ml: 30 },
  { label: 'Glass (150ml)', ml: 150 },
  { label: 'Whole can / bottle', ml: null },
];

/** A sensible serve for a drink: shots for spirits, glasses for wine, whole units for anything small. */
export function defaultServeMl(kind: DrinkKind, sizeMl: number | null): number | null {
  if (kind === 'spirit') return 30;
  if (kind === 'wine') return 150;
  if (kind === 'beer' || kind === 'cider' || kind === 'premix') return null;
  return sizeMl !== null && sizeMl > 500 ? 150 : null;
}

/**
 * Whether the open bottle is low enough to be worth a look: under the threshold, and either poured
 * from since it was last checked or not checked for a month. Whole-unit bottles are counted, not estimated.
 */
export function needsCheck(bottle: Bottle, now = new Date()): boolean {
  if (bottle.quantity === 0 || isWholeUnit(bottle) || bottle.level >= CHECK_THRESHOLD) return false;
  const sinceCheckMs = now.getTime() - new Date(bottle.levelCheckedAt).getTime();
  return bottle.poursSinceCheck > 0 || sinceCheckMs >= RECHECK_AFTER_DAYS * 24 * 60 * 60_000;
}

/** Units left: the unopened ones plus however much of the open one, e.g. 1.5 for two bottles with one half full. */
export function unitsLeft(bottle: Pick<Bottle, 'quantity' | 'level' | 'serveMl' | 'sizeMl'>): number {
  if (bottle.quantity === 0) return 0;
  return isWholeUnit(bottle) ? bottle.quantity : bottle.quantity - 1 + bottle.level;
}

/** Flagged by hand, or kept stocked and at/below its threshold. */
export function onShoppingList(bottle: Bottle): boolean {
  return bottle.wantMore || (bottle.restockAt !== null && unitsLeft(bottle) <= bottle.restockAt);
}

/** A starting threshold for "keep stocked": a quarter of a bottle, or two cans. */
export function defaultRestockAt(bottle: Pick<Bottle, 'serveMl' | 'sizeMl'>): number {
  return isWholeUnit(bottle) ? 2 : 0.25;
}

/** "bottle" or "can", for the units in "when fewer than 2 cans are left". */
export function unitNoun(bottle: Pick<Bottle, 'kind'>): string {
  return bottle.kind === 'beer' || bottle.kind === 'cider' ? 'can' : 'bottle';
}

/** Bottles tracked by the whole unit (no serve, or no size to divide by). */
export function isWholeUnit(bottle: Pick<Bottle, 'serveMl' | 'sizeMl'>): boolean {
  return bottle.serveMl === null || bottle.sizeMl === null;
}

/** e.g. "About half left", "Nearly empty · 2 unopened", "6 left", "Finished". */
export function describeLevel(bottle: Bottle): string {
  if (bottle.quantity === 0) return 'Finished';
  if (isWholeUnit(bottle)) return `${bottle.quantity} left`;
  const open =
    bottle.level < 0.01
      ? 'Empty'
      : bottle.level >= 0.95
      ? 'Full'
      : bottle.level >= 0.7
        ? 'About ¾ left'
        : bottle.level >= 0.45
          ? 'About half left'
          : bottle.level >= 0.2
            ? 'About ¼ left'
            : 'Nearly empty';
  const unopened = bottle.quantity - 1;
  return unopened > 0 ? `${open} · ${unopened} unopened` : open;
}

/** The picture to show: the owner's own photo if there is one, else the catalogue's. */
export function pictureOf(bottle: Pick<Bottle, 'photoUrl' | 'imageUrl'>): string | null {
  return bottle.photoUrl ?? bottle.imageUrl;
}

export function formatSize(sizeMl: number | null): string | null {
  if (sizeMl === null) return null;
  return sizeMl >= 1000 && sizeMl % 50 === 0 ? `${sizeMl / 1000} L` : `${sizeMl} ml`;
}
